import * as React from 'react'
import {
	createStyles,
	makeStyles,
	Theme,
	Input,
	InputAdornment,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from '@material-ui/core'
import { observer } from 'mobx-react'
import Icon from '~components/gui/Icon'
import { FareDiscount, FareDiscountTier, UseRate } from '~store/models/FareDiscount'

// ███████╗████████╗██╗   ██╗██╗     ███████╗███████╗
// ██╔════╝╚══██╔══╝╚██╗ ██╔╝██║     ██╔════╝██╔════╝
// ███████╗   ██║    ╚████╔╝ ██║     █████╗  ███████╗
// ╚════██║   ██║     ╚██╔╝  ██║     ██╔══╝  ╚════██║
// ███████║   ██║      ██║   ███████╗███████╗███████║
// ╚══════╝   ╚═╝      ╚═╝   ╚══════╝╚══════╝╚══════╝
//

const styles = ({ palette, spacing }: Theme) =>
	createStyles({
		container: {
			marginTop: spacing(1),
		},
		tableContainer: {
			marginBottom: spacing(2),
		},
		table: {
			'& .MuiTableCell-root': {
				padding: spacing(0.75, 1),
				borderBottom: `1px solid ${palette.divider}`,
			},
			'& .MuiTableCell-head': {
				fontWeight: 600,
				whiteSpace: 'nowrap',
				fontSize: '0.8rem',
				color: palette.text.secondary,
			},
		},
		headerRow: {
			'& .MuiTableCell-root': {
				borderBottom: `2px solid ${palette.divider}`,
			},
		},
		tierLabelCell: {
			verticalAlign: 'middle',
			whiteSpace: 'nowrap',
			width: 80,
		},
		inputCell: {
			width: 120,
		},
		deleteCell: {
			width: 40,
			padding: spacing(0.5),
		},
		input: {
			width: '100%',
			'& input': {
				textAlign: 'right',
				paddingRight: spacing(0.5),
			},
		},
		footer: {
			display: 'flex',
			alignItems: 'flex-end',
			gap: spacing(3),
			flexWrap: 'wrap',
		},
		addButton: {},
		deleteButton: {
			opacity: 0.5,
			'&:hover': {
				opacity: 1,
			},
		},
		useRateFormControl: {
			minWidth: 220,
		},
	})
const useStyles = makeStyles(styles)

//  ██████╗ ██████╗ ███╗   ███╗██████╗  ██████╗ ███╗   ██╗███████╗███╗   ██╗████████╗
// ██╔════╝██╔═══██╗████╗ ████║██╔══██╗██╔═══██╗████╗  ██║██╔════╝████╗  ██║╚══██╔══╝
// ██║     ██║   ██║██╔████╔██║██████╔╝██║   ██║██╔██╗ ██║█████╗  ██╔██╗ ██║   ██║
// ██║     ██║   ██║██║╚██╔╝██║██╔═══╝ ██║   ██║██║╚██╗██║██╔══╝  ██║╚██╗██║   ██║
// ╚██████╗╚██████╔╝██║ ╚═╝ ██║██║     ╚██████╔╝██║ ╚████║███████╗██║ ╚████║   ██║
//  ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═╝      ╚═════╝ ╚═╝  ╚═══╝╚══════╝╚═╝  ╚═══╝   ╚═╝
//

export interface ITieredDiscountFieldProps {
	value: FareDiscount | null
	onChange: (value: FareDiscount) => void
}

const TieredDiscountField = observer(({ value, onChange }: ITieredDiscountFieldProps) => {
	const classes = useStyles()

	// Ensure we always have at least one tier
	const tiers = value?.tiers ?? []
	const hasMultipleTiers = tiers.length > 1

	// Update a specific tier field
	const updateTier = (tier: FareDiscountTier, field: keyof FareDiscountTier, fieldValue: number | null) => {
		tier.setAttribute(field as string, fieldValue)
	}

	// Update use_rate
	const updateUseRate = (useRate: UseRate) => {
		if (value) {
			value.setAttribute('use_rate', useRate)
		}
	}

	// Add a new tier
	const addTier = () => {
		if (!value) {
			// Create new FareDiscount with two tiers (first staffel setup)
			const newDiscount = new FareDiscount({
				tiers: [new FareDiscountTier({}), new FareDiscountTier({})],
			})
			onChange(newDiscount)
		} else {
			value.addTier()
		}
	}

	// Remove a tier
	const removeTier = (tierIndex: number) => {
		if (!value || tiers.length <= 1) return
		value.removeTier(tierIndex)
	}

	// Parse input value to number or null
	const parseValue = (v: string): number | null => {
		if (v === '' || v === null || v === undefined) return null
		const parsed = parseFloat(v)
		return isNaN(parsed) ? null : parsed
	}

	// If no value yet, show empty state with button
	if (!value || tiers.length === 0) {
		return (
			<div className={classes.container}>
				<Button
					variant='outlined'
					size='small'
					startIcon={<Icon name='plus' size={14} />}
					onClick={() => {
						const newDiscount = new FareDiscount({
							tiers: [new FareDiscountTier({})],
						})
						onChange(newDiscount)
					}}>
					Korting configureren
				</Button>
			</div>
		)
	}

	// Column headers - discount types as columns
	const columns = [
		...(hasMultipleTiers ? [{ key: 'from_amount', label: 'Vanaf bedrag', prefix: '€' }] : []),
		{ key: 'percentage', label: 'Korting (standaard)', suffix: '%' },
		{ key: 'percentage_morning_peak', label: 'Ochtendspits', suffix: '%' },
		{ key: 'percentage_afternoon_peak', label: 'Middagspits', suffix: '%' },
		{ key: 'percentage_outside_peak', label: 'Daluren', suffix: '%' },
	]

	return (
		<div className={classes.container}>
			<TableContainer className={classes.tableContainer}>
				<Table className={classes.table} size='small'>
					<TableHead>
						<TableRow className={classes.headerRow}>
							<TableCell className={classes.tierLabelCell}></TableCell>
							{columns.map(col => (
								<TableCell key={col.key} className={classes.inputCell}>
									{col.label}
								</TableCell>
							))}
							{hasMultipleTiers && <TableCell className={classes.deleteCell}></TableCell>}
						</TableRow>
					</TableHead>
					<TableBody>
						{tiers.map((tier, tierIndex) => (
							<TableRow key={tierIndex}>
								<TableCell className={classes.tierLabelCell}>
									<Typography variant='body2'>{tiers.length === 1 ? 'Korting' : `Staffel ${tierIndex + 1}`}</Typography>
								</TableCell>
								{columns.map(col => (
									<TableCell key={col.key} className={classes.inputCell}>
										<Input
											className={classes.input}
											type='number'
											startAdornment={
												col.prefix ? <InputAdornment position='start'>{col.prefix}</InputAdornment> : undefined
											}
											endAdornment={
												col.suffix ? <InputAdornment position='end'>{col.suffix}</InputAdornment> : undefined
											}
											value={(tier as any)[col.key] ?? ''}
											onChange={e => updateTier(tier, col.key as keyof FareDiscountTier, parseValue(e.target.value))}
											placeholder={col.key === 'from_amount' && tierIndex === 0 ? '0' : ''}
											inputProps={{ min: 0, ...(col.suffix === '%' ? { max: 100 } : { step: 0.01 }) }}
										/>
									</TableCell>
								))}
								{hasMultipleTiers && (
									<TableCell className={classes.deleteCell}>
										<IconButton size='small' className={classes.deleteButton} onClick={() => removeTier(tierIndex)}>
											<Icon name='trash' size={14} />
										</IconButton>
									</TableCell>
								)}
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<div className={classes.footer}>
				<Button
					className={classes.addButton}
					variant='outlined'
					size='small'
					startIcon={<Icon name='plus' size={14} />}
					onClick={addTier}>
					Staffel toevoegen
				</Button>

				{hasMultipleTiers && (
					<FormControl className={classes.useRateFormControl} size='small'>
						<InputLabel>Bereken op basis van</InputLabel>
						<Select
							value={value.use_rate ?? UseRate.Discounted}
							onChange={e => updateUseRate(e.target.value as UseRate)}
							label='Bereken op basis van'>
							<MenuItem value={UseRate.Discounted}>kortingstarief</MenuItem>
							<MenuItem value={UseRate.Full}>volledig tarief</MenuItem>
						</Select>
					</FormControl>
				)}
			</div>
		</div>
	)
})

export default TieredDiscountField
